/**
 * @file
 * Language, script, and region information.
 */


import $date from './date';
import $num from './num';


const ietfParse = str => str.match(/^([a-z]{2,3})(?:[_-]([a-z]{3})(?=$|[_-]))?(?:[_-]([a-z]{4})(?=$|[_-]))?(?:[_-]([a-z]{2}|\d{3})(?=$|[_-]))?/i);
const ietfStringify = part => part.slice(1).filter(Boolean).join('-');
const autoScript = part => part[1] == 'zh' ? part[4] == 'CN' || part[4] == 'SG' ? 'Hans' : 'Hant' : void 0;
const modernize = str => str.replace(/^(iw|yi|id)(?=-|$)/, lang => ({iw:'he',yi:'ji',id:'in'}[lang]));

const supported = ['en','de','es','fr','it','zh','ja','ru','hu','he','ar'];
const all = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'it', name: 'Italiano' },
    { code: 'ru', name: 'Pусский' },
    { code: 'hu', name: 'Magyar' },
    { code: 'zh-Hans', name: '中文简体' },
    { code: 'zh-Hant', name: '中文繁體' },
    { code: 'ja', name: '日本語' },
    { code: 'ar', name: 'العربية' },
    { code: 'he', name: 'עִברִית' }
];

const translations = _i18n.translations;


const $locale = Vue.observable(/** @alias $locale */ {
    /** @member {Array} - An array of all locales supported by Lib.Common. */
    all,
    /** @member {string} - The current locale code. This may differ from [`$app.option.language`](#/global/app), which is the requested locale. */
    current: null,
    /** @member {string} - The language field of the current locale. */
    language: null,
    /** @member {string} - The extlang field of the current locale, if any. */
    extlang: null,
    /** @member {string} - The script field of the current locale, if any. */
    script: null,
    /** @member {string} - The region field of the current locale, if any. */
    region: null,
    /** @member {string} - Human readable name of the current locale, in that locale's language. */
    name: null,
    /** @member {string} - Version of current language string compatible with Account Management backend. */
    iblang: null,

    /** @private */
    data: {},


    /** @private */
    set(language) {
        if (language == null) {
            language = $locale.current;
        } else {
            const part = ietfParse(language);
            if (!part) {
                throw new RangeError('invalid language: ' + language);
            }

            const current = ietfStringify(part);
            if ($locale.current != current) {
                /** @ignore */
                $locale.current = current;
                /** @ignore */
                $locale.language = part[1];
                /** @ignore */
                $locale.extlang = part[2];
                /** @ignore */
                $locale.script = part[3] || autoScript(part);
                /** @ignore */
                $locale.region = part[4];
                /** @ignore */
                $locale.name =
                    (all.find(e => e.code == $locale.language + '-' + $locale.script) ||
                     all.find(e => e.code == $locale.language) ||
                     all[0]).name;
                /** @ignore */
                $locale.iblang = supported.includes(part[1])
                    ? part[1] == 'zh'
                        ? $locale.script == 'Hans' ? 'zh_CN' : 'zh_TW'
                        : part[1]
                    : 'en';

                /** @ignore */
                $locale.data = {
                    ...translations['en'],
                    ...translations[$locale.language],
                    ...translations[$locale.language + '-' + $locale.script]
                };

                $date._use(current);
                $num._use($locale.englishNumbers && ($locale.language != 'ar' && $locale.language != 'he') ? 'en' : current);
                document.documentElement.lang = current;
            }
        }
    }
});


/**
 * @private
 * Change the current language. This populates various `$locale` fields, which may trigger `ib-application` to load
 * translation data via object or fetch. It also populates Lib.Common's own translation data.
 */
function setLanguage(language, englishNumbers) {
    try {
        if (language == null) {
            // find first supported language in navigator.languages, with region if available
            const languages = (navigator.languages || [navigator.language]).map(modernize);
            for (let i = 0; i < languages.length; i++) {
                const part = ietfParse(languages[i]);
                if ((language == null && supported.includes(part[1])) || (language == part[1] && part[4])) {
                    language = languages[i];
                    if (part[4]) {
                        break;
                    }
                }
            }
        } else {
            language = modernize(language);
        }

        /** @ignore */
        $locale.englishNumbers = englishNumbers;
        $locale.set(language || 'en');
    } catch (e) {
        $locale.set('en');
        console.error(e.message);
    }
}


export {
    setLanguage
};
export default $locale;
